/* eslint-disable i18next/no-literal-string */
import { useEffect, useRef, useState } from 'react';
import { preparePhoto, composite } from '../lib/photoPreparation.js';

export default function BrowserPhotoLab() {
  const [file, setFile] = useState<File | null>(null),
    [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(
    'Choose a photo. Experimental on-device prototype; no server processing.',
  );
  const [output, setOutput] = useState('');
  const worker = useRef<Worker | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const generation = useRef(0);
  function stop() {
    generation.current++;
    worker.current?.terminate();
    worker.current = null;
    if (timer.current) clearTimeout(timer.current);
    setBusy(false);
  }
  useEffect(
    () => () => {
      generation.current++;
      worker.current?.terminate();
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  async function run() {
    if (!file) return;
    stop();
    const current = generation.current;
    setBusy(true);
    setOutput('');
    setStatus('Preparing face locally…');
    const began = performance.now();
    timer.current = setTimeout(() => {
      stop();
      setStatus('Stopped at 180-second time limit');
    }, 180000);
    try {
      const prepared = await preparePhoto(file);
      if (current !== generation.current) return;
      const instance = new Worker(new URL('../lib/photoPipeline.worker.ts', import.meta.url), {
        type: 'module',
      });
      worker.current = instance;
      instance.onerror = (e) => {
        stop();
        setStatus(e.message);
      };
      instance.onmessage = (e) => {
        if (worker.current !== instance) return;
        if (!e.data.done) {
          setStatus(`Running locally: ${e.data.phase}`);
          return;
        }
        try {
          if (e.data.error) throw new Error(`${e.data.failedPhase ?? 'worker'}: ${e.data.error}`);
          setOutput(composite(prepared.source, prepared.matrix, e.data.pixels));
          setStatus(
            `Complete — ${Math.round(performance.now() - began)} ms total. Experimental crying preset.`,
          );
        } catch (error) {
          setStatus(String(error));
        } finally {
          stop();
        }
      };
      instance.postMessage({ image: prepared.image }, [prepared.image.buffer]);
    } catch (error) {
      if (current === generation.current) {
        stop();
        setStatus(String(error));
      }
    }
  }
  return (
    <section>
      <h2>Phase 9 — Browser-only crying photo prototype</h2>
      <p>
        One photo, built-in experimental expression, no reference upload. About 367 MiB of models
        plus runtimes: use Wi-Fi. WebGPU required. Experimental phone test; Safari may fail
        because of GPU compatibility or memory limits. Photos stay on your device.
      </p>
      <label>
        Original photo{' '}
        <input
          type="file"
          accept="image/*"
          disabled={busy}
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null);
            setOutput('');
          }}
        />
      </label>
      <button disabled={!file || busy} onClick={run}>
        Apply crying filter locally
      </button>
      {busy && (
        <button
          onClick={() => {
            stop();
            setStatus('Cancelled');
          }}
        >
          Cancel photo processing
        </button>
      )}
      <p role="status">{status}</p>
      {output && (
        <>
          <img
            src={output}
            alt="Browser-generated crying result"
            style={{ maxWidth: 570, width: '100%' }}
          />
          <p>
            <a download="crying-photo.png" href={output}>
              Download filtered photo
            </a>
          </p>
        </>
      )}
    </section>
  );
}
